import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, FC } from 'react';
// @ts-ignore
import useLocalStorage from 'use-localstorage-hook';
// @ts-ignore
import { getSortedList, nextType } from './listHelpers';
// @ts-ignore
import { isFunction } from '../../utilities/check';
// @ts-ignore
import ListHeader from './ListHeader';
// @ts-ignore
import MobileListView from './MobileListView';
// @ts-ignore
import Pager from './Pager';
// @ts-ignore
import SimpleTextFilter from './SimpleTextFilter';
import keyBy from 'lodash/keyBy';
import { isEmpty } from 'lodash';
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Popover,
} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import { ArrowDropDown, ArrowDropUp, InfoOutlined } from '@material-ui/icons';
// @ts-ignore
import SelectionContext from '../../../features/selection/SelectionContext';
// @ts-ignore
import SelectionView from '../../../features/selection/SelectionView';
// @ts-ignore
import BulkPrintView from '../../../components/entities/procedures/BulkPrintView';

const useStyles = makeStyles(theme => ({
  flexWrapper: {
    minHeight: 0,
    flex: 1,

    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
    },
  },
  container: {
    height: '100%',
    // Fix white box at scrollbar intersection
    '&::-webkit-scrollbar-corner': {
      backgroundColor: 'transparent',
    },
  },
  tableCell: {
    whiteSpace: 'nowrap',
    color: theme.palette.text.secondary,
  },
  sortable: {
    cursor: 'pointer',
  },
}));

let prev: { collection: string; id: string | number } | undefined;

const hasRenderFunction = (node: any) => !!node?.type?.render;

interface ScrollerProps {
  collection: string;
  id: string | number;
  children: React.ReactElement;
}

/**
 * Scroller ensures that when navigating back to the list,
 * the last interacted row is scrolled into view.
 */
export const Scroller: FC<ScrollerProps> = ({ collection, id, children }) => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (ref.current) {
      const thisRef = ref.current;
      function handleMouseUp() {
        prev = { collection, id };
      }
      thisRef.addEventListener('mouseup', handleMouseUp);
      return () => thisRef.removeEventListener('mouseup', handleMouseUp);
    }
  }, [collection, id]);

  useEffect(() => {
    if (prev && prev.collection === collection && prev.id === id) {
      if (ref.current) {
        if (ref.current.previousElementSibling) {
          ref.current.previousElementSibling.scrollIntoView();
        } else {
          ref.current.scrollIntoView();
        }
      }
    }
  }, [collection, id]);

  return React.cloneElement(children, { ref: hasRenderFunction(children) ? ref : null });
};

export interface Column {
  title: string;
  hidden?: boolean;
  sortDisabled?: boolean; // Added types
  headerCellStyle?: React.CSSProperties; // Added types
  InfoComponent?: React.ComponentType; // Added types
  [key: string]: any;
}

export interface SortState {
  type?: 'ASC' | 'DESC';
  title?: string;
}

interface ListProps {
  Component: React.ComponentType<any>;
  hideColumns?: boolean;
  columns: Column[];
  defaultDisplayColumns?: string[];
  showUrlProvider?: (item: any) => string;
  idProvider: (item: any) => string | number;
  onViewClick?: (data: any, index: number) => void;
  getList?: (data: any) => any[];
  data: any[];
  history?: any;
  location?: any;
  highlightedProvider?: (item: any) => boolean;
  highlightedRowStyles?: any;
  highlightedRowStyleProvider?: (item: any) => any;
  containsSeparator?: boolean;
  tableKey: string;
  useColumnSelection?: boolean;
  useCSVExport?: boolean;
  MobileItemComponent?: React.ComponentType<any>;
  simpleFilter?: boolean;
  defaultSort?: SortState;
  customExportCSV?: any;
  customExportXLSX?: any;
  customExportPDF?: any;
  exportFileName?: string;
  // New props for controlled sorting
  onSort?: (sort: SortState) => void;
  currentSort?: SortState;
  // Custom content for left side of list header (e.g., stat chips)
  HeaderLeft?: React.ReactNode;
}

/**
 * List component renders the table view.
 * It uses local storage to persist both the column order and visibility.
 */
const List: FC<ListProps> = ({
  Component,
  hideColumns,
  columns,
  defaultDisplayColumns,
  showUrlProvider,
  idProvider,
  onViewClick,
  getList = (d: any) => d, // Default identity function if missing
  data,
  history,
  location,
  highlightedProvider,
  highlightedRowStyles,
  highlightedRowStyleProvider,
  containsSeparator,
  tableKey,
  useColumnSelection,
  useCSVExport,
  MobileItemComponent,
  simpleFilter,
  defaultSort,
  customExportCSV,
  customExportXLSX,
  customExportPDF,
  exportFileName,
  // New props for controlled sorting
  onSort,
  currentSort: controlledSort,
  // Custom content for left side of list header
  HeaderLeft,
}) => {
  const classes = useStyles();
  const selectionContext = useContext(SelectionContext);

  const handleViewClick = (data: any, index: number) => () => {
    if (isFunction(onViewClick)) {
      onViewClick!(data, index);
    }
  };

  // Build column options from provided columns.
  // Here we define the new format: each column has { title, visible }.
  const columnOptions = columns.map(col => ({
    title: col.title,
    // Use the "hidden" flag (if any) as default visibility.
    visible: !col.hidden,
  }));

  // If defaultDisplayColumns is provided, override the default visibility.
  const defaultSettings = defaultDisplayColumns
    ? columnOptions.map(col => ({
        ...col,
        visible: defaultDisplayColumns.includes(col.title),
      }))
    : columnOptions;

  // Create a mapping for cleanup.
  const columnKeys = keyBy(columnOptions, 'title');

  // Retrieve stored settings using local storage hook.
  const [storedSettings, setStoredSettings] = useLocalStorage(tableKey, defaultSettings);

  /**
   * If storedSettings are in the old format (missing "visible"),
   * migrate them so that each column gets a visible flag.
   * (Assume all stored columns were visible in the old format.)
   * New-format prefs keep stored visibility/order and append any columns
   * added in code, using each column's default `hidden` flag.
   */
  const migrateSettings = (settings: any[], defaults: any[]) => {
    if (Array.isArray(settings) && settings.length > 0) {
      // Handle old format without visible flag
      if (settings[0].visible === undefined) {
        const storedTitles = settings.map(s => s.title);
        return defaults.map(setting => ({
          ...setting,
          visible: storedTitles.includes(setting.title),
        }));
      }

      const storedTitles = new Set(settings.map(s => s.title));
      return [
        ...settings.map(setting => ({ ...setting })),
        ...defaults.filter(setting => !storedTitles.has(setting.title)),
      ];
    }
    return settings;
  };

  // Clean up stored settings
  const cleanColumnSettings = migrateSettings(storedSettings, defaultSettings).filter(
    (setting: any) => setting.title in columnKeys
  );

  // Tables without a column picker always use the code-defined columns and
  // order. With a picker, stored prefs win; columns added after those prefs
  // were saved use each column's default `hidden` flag.
  const visibleColumns = !useColumnSelection
    ? columns.filter(col => !col.hidden)
    : columns
        .filter(col => {
          const setting = cleanColumnSettings.find((s: any) => s.title === col.title);
          return setting ? setting.visible : !col.hidden;
        })
        .sort((a, b) => {
          const aIndex = cleanColumnSettings.findIndex((s: any) => s.title === a.title);
          const bIndex = cleanColumnSettings.findIndex((s: any) => s.title === b.title);
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });

  const [_currentSort, _setCurrentSort] = useLocalStorage(
    `${tableKey}-sort`,
    defaultSort || { type: undefined, title: undefined }
  );

  // Use controlled sort if provided, otherwise internal storage

  const currentSort = controlledSort || (isEmpty(_currentSort) ? defaultSort || {} : _currentSort);

  const bulkPrintSourcePage = useMemo(() => {
    const pathname = location?.pathname || '';
    if (/\/scheduled-patients(?:\/|$)/.test(pathname)) return 'preop-registration';
    if (/\/registration-packages(?:\/|$)/.test(pathname)) return 'registration-packages';
    return undefined;
  }, [location?.pathname]);

  const setCurrentSort = (newSort: SortState) => {
    if (onSort) {
      onSort(newSort);
    } else {
      _setCurrentSort(newSort);
    }
  };

  // Add cleanup function for corrupted storage
  useEffect(() => {
    try {
      const cleanupStorage = () => {
        // Clear potentially corrupted sort settings
        const sortKey = `${tableKey}-sort`;
        const currentSort = localStorage.getItem(sortKey);
        if (currentSort && !isValidSort(currentSort)) {
          localStorage.removeItem(sortKey);
        }

        // Clear potentially corrupted column settings
        const columnSettings = localStorage.getItem(tableKey);
        if (columnSettings && !isValidColumnSettings(columnSettings)) {
          localStorage.removeItem(tableKey);
        }
      };

      const isValidSort = (sortString: string) => {
        try {
          const sort = JSON.parse(sortString);
          return sort && typeof sort === 'object' && (!sort.type || ['ASC', 'DESC'].includes(sort.type));
        } catch {
          return false;
        }
      };

      const isValidColumnSettings = (settingsString: string) => {
        try {
          const settings = JSON.parse(settingsString);
          return (
            Array.isArray(settings) &&
            settings.every(
              s => s && typeof s === 'object' && typeof s.title === 'string' && typeof s.visible === 'boolean'
            )
          );
        } catch {
          return false;
        }
      };

      cleanupStorage();
    } catch (error) {
      console.warn('Error cleaning up storage:', error);
    }
  }, [tableKey]);

  // Render mobile view if needed.
  if (window.innerWidth < 992 && MobileItemComponent) {
    return (
      <MobileListView
        Component={Component}
        hideColumns={hideColumns}
        columns={columns}
        showUrlProvider={showUrlProvider}
        idProvider={idProvider}
        onViewClick={onViewClick}
        getList={getList}
        data={data}
        history={history}
        location={location}
        highlightedProvider={highlightedProvider}
        highlightedRowStyles={highlightedRowStyles}
        highlightedRowStyleProvider={highlightedRowStyleProvider}
        containsSeparator={containsSeparator}
        tableKey={tableKey}
        useColumnSelection={useColumnSelection}
        useCSVExport={false}
        MobileItemComponent={MobileItemComponent}
      />
    );
  }

  const sortColumn = columns.find(col => col.title === currentSort.title && currentSort.type);

  // When onSort is provided, sorting is server-side - skip client-side sorting
  // This prevents double-sorting issues with paginated lists where the server
  // already returns data in the correct order
  const isServerSideSorting = !!onSort;
  const list =
    sortColumn && !isServerSideSorting
      ? getSortedList(getList(data), containsSeparator, currentSort, sortColumn, data)
      : getList(data);

  return (
    <Fragment>
      <ListHeader
        showColumnSelection={!hideColumns && useColumnSelection}
        showCSVExport={useCSVExport}
        selectedColumns={cleanColumnSettings}
        setSelectedColumns={setStoredSettings}
        columns={columns}
        columnOptions={columnOptions}
        tableKey={tableKey}
        list={list}
        data={data}
        customExportCSV={customExportCSV}
        customExportXLSX={customExportXLSX}
        customExportPDF={customExportPDF}
        exportFileName={exportFileName}
        HeaderLeft={HeaderLeft}
      >
        {selectionContext && (
          <SelectionView
            context={selectionContext}
            getItem={(id: any) => list.find((item: any) => String(idProvider(item)) === id)}
          >
            {(items: any) => <BulkPrintView items={items} sourcePage={bulkPrintSourcePage} />}
          </SelectionView>
        )}
      </ListHeader>

      <SimpleTextFilter onChange={(val: any) => val} active={simpleFilter} data={list} columns={columns}>
        {(filtered: any[]) => (
          <Pager data={filtered} currentSort={currentSort} tableKey={tableKey}>
            {(pagedData: any[]) => (
              <Box className={classes.flexWrapper}>
                <TableContainer className={classes.container} tabIndex={0}>
                  <Table stickyHeader style={{ borderCollapse: 'collapse' }}>
                    {hideColumns ? null : (
                      <TableHead>
                        <TableRow>
                          {selectionContext && (
                            <TableCell className={classes.tableCell}>
                              <HeaderSelectAllCheckbox list={list} idProvider={idProvider} />
                            </TableCell>
                          )}
                          {visibleColumns.map((column, i) => (
                            <HeaderWithInfo
                              key={i}
                              column={column}
                              currentSort={currentSort}
                              setCurrentSort={setCurrentSort}
                              classes={classes}
                            />
                          ))}
                        </TableRow>
                      </TableHead>
                    )}
                    <TableBody>
                      {pagedData.map((item, i) => (
                        <Scroller
                          key={`${idProvider(item)}-${i}`}
                          collection={location?.pathname || ''}
                          id={idProvider(item)}
                        >
                          <Component
                            history={history}
                            location={location}
                            columns={visibleColumns}
                            item={item}
                            context={{ data }}
                            index={i}
                            showUrlProvider={showUrlProvider}
                            idProvider={idProvider}
                            onClick={handleViewClick(item, i)}
                            highlighted={highlightedProvider ? highlightedProvider(item) : false}
                            highlightedRowStyles={
                              highlightedRowStyleProvider
                                ? highlightedRowStyleProvider(item)
                                : highlightedProvider && highlightedRowStyles && highlightedProvider(item)
                                  ? highlightedRowStyles
                                  : undefined
                            }
                          />
                        </Scroller>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Pager>
        )}
      </SimpleTextFilter>
    </Fragment>
  );
};

interface HeaderWithInfoProps {
  column: Column;
  currentSort: SortState;
  setCurrentSort: (sort: SortState) => void;
  classes: any;
}

const HeaderWithInfo: FC<HeaderWithInfoProps> = ({ column, currentSort, setCurrentSort, classes }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { title, sortDisabled, headerCellStyle, InfoComponent } = column;

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: any) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleSort = () => {
    if (!sortDisabled && !anchorEl) {
      setCurrentSort({
        type: title !== currentSort.title ? 'ASC' : nextType(currentSort.type),
        title,
      });
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'info-popover' : undefined;

  const getSortAriaLabel = () => {
    if (sortDisabled) return undefined;
    if (currentSort.title === title) {
      return currentSort.type === 'ASC'
        ? `Column ${title} is currently sorted in ascending order. Click to sort in descending order.`
        : `Column ${title} is currently sorted in descending order. Click to sort in ascending order.`;
    }
    return `Click to sort by ${title}`;
  };

  const getSortTooltip = () => {
    if (sortDisabled) return undefined;
    if (currentSort.title === title) {
      return currentSort.type === 'ASC' ? 'Sorted ascending' : 'Sorted descending';
    }
    return 'Click to sort';
  };

  return (
    <TableCell
      key={title}
      // @ts-ignore
      disabled={sortDisabled}
      className={clsx(classes.tableCell, {
        [classes.sortable]: !sortDisabled,
      })}
      onClick={handleSort}
      aria-label={getSortAriaLabel()}
      title={getSortTooltip()}
      role={sortDisabled ? undefined : 'columnheader button'}
      aria-sort={
        sortDisabled || currentSort.title !== title
          ? undefined
          : currentSort.type === 'ASC'
            ? 'ascending'
            : 'descending'
      }
    >
      <Box display="flex" alignItems="center" style={headerCellStyle}>
        <div>{title}</div>
        {InfoComponent && (
          <IconButton size="small" aria-describedby={id} onClick={handleInfoClick}>
            <InfoOutlined fontSize="small" color="primary" />
          </IconButton>
        )}
        <Box display="flex" alignItems="center" justifyContent="center" ml={0.5} style={{ minWidth: 20, height: 20 }}>
          {!sortDisabled && (
            <>
              {currentSort.title === title && currentSort.type === 'ASC' && (
                <ArrowDropDown fontSize={'medium'} style={{ color: '#00A7F7' }} />
              )}
              {currentSort.title === title && currentSort.type === 'DESC' && (
                <ArrowDropUp fontSize={'medium'} style={{ color: '#00A7F7' }} />
              )}
              {currentSort.title !== title && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    opacity: 0.5,
                    fontSize: '0.9em',
                    lineHeight: '0.3em',
                    width: 20,
                    height: 20,
                    gap: '-2px',
                  }}
                >
                  <ArrowDropUp
                    style={{
                      fontSize: '1.6em',
                      marginBottom: '-8px',
                      color: 'inherit',
                    }}
                  />
                  <ArrowDropDown
                    style={{
                      fontSize: '1.6em',
                      marginTop: '-8px',
                      color: 'inherit',
                    }}
                  />
                </Box>
              )}
            </>
          )}
          {sortDisabled && <div style={{ width: 24, height: 24 }} />}
        </Box>
      </Box>
      {InfoComponent && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* @ts-ignore */}
          <InfoComponent />
        </Popover>
      )}
    </TableCell>
  );
};

const HeaderSelectAllCheckbox = ({ list, idProvider }: { list: any[]; idProvider: (item: any) => string | number }) => {
  const selectionContext = useContext(SelectionContext) as any;
  const setAllSelections = selectionContext.useSetAllSelections();

  const selectedKeys = selectionContext.useSelected();

  const allKeys = list.map(idProvider);
  const isChecked = selectedKeys.length === allKeys.length;
  const isIndeterminate = selectedKeys.length > 0 && selectedKeys.length < allKeys.length;

  const handleSelectAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (selectedKeys.length > 0) {
        setAllSelections(allKeys, false);
        return;
      }
      setAllSelections(allKeys, e.target.checked);
    },
    [setAllSelections, allKeys, selectedKeys.length]
  );

  return <Checkbox indeterminate={isIndeterminate} checked={isChecked} onChange={handleSelectAll} />;
};

export default List;
